import { CardHeadingGroup, CardNav } from '@storeblocks/card';
import { type FC } from 'react';
import styled from 'styled-components';

import { QueryState } from '@/api/components/QueryState';
import { useIHubMonthlyReportsQuery } from '@/api/reports/reportsApi';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { isNotReady } from '@/api/utils/isNotReady';
import { CardColumn, Row } from '@/components/Grid';
import { InternalLink } from '@/components/InternalLink';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { SurfaceWidget } from '@/components/Widget';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { ReportSource } from '@/pages/Reports/types/ReportModel';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { downloadFilesFromIhub } from '@/util/downloadFilesFromIHub';

import { ReportListItem } from './ReportListItem';

export const LatestMonthlyReportsWidget: FC = () => {
  const fms = useTexts();
  const texts = fms.widgets.reports.latestMonthlyReports;

  const getTrackId = useGetTrackId();
  const cmId = useOrganizationCmId();

  const monthlyReportsQuery = useIHubMonthlyReportsQuery();

  return (
    <WithGap gap="32">
      <h2>{texts.header}</h2>
      <Row>
        <StyledCardColumn $span={8}>
          <StyledSurfaceWidget data-test="latest-monthly-reports-widget">
            <WithGap direction="row" gap="8">
              <h3>{texts.title}</h3>
              <ConnectPopover
                id="latest-monthly-reports"
                title={texts.help.title}
                description={texts.help.description}
              ></ConnectPopover>
            </WithGap>
            {isNotReady(monthlyReportsQuery) ? (
              <QueryState
                query={monthlyReportsQuery}
                customTexts={{
                  noData: {
                    description: texts.noData,
                  },
                  error: texts.error,
                }}
              />
            ) : (
              <List>
                {monthlyReportsQuery.data?.map((report) => {
                  return (
                    <ReportListItem
                      key={report.id}
                      filename={report.name}
                      fileId={report.id}
                      onDownload={() =>
                        downloadFilesFromIhub(
                          [report.id],
                          ReportSource.IHubOrganization,
                        )
                      }
                    />
                  );
                })}
              </List>
            )}
          </StyledSurfaceWidget>
        </StyledCardColumn>
        <CardColumn $span={4}>
          <WithGap>
            <CardNav
              icon="files"
              data-test="reports-page-portfolio-link"
              orientation="horizontal"
            >
              {(ref: React.Ref<HTMLAnchorElement>) => (
                <CardHeadingGroup
                  ref={ref}
                  heading={texts.archiveLink}
                  as={InternalLink}
                  to={`/${cmId}/reports/portfolio`}
                  data-trackid={getTrackId(
                    'go-to-reports-archive',
                    TrackingElement.Link,
                  )}
                />
              )}
            </CardNav>
            <CardNav
              icon="graph_lines"
              data-test="reports-page-funds-link"
              orientation="horizontal"
            >
              {(ref: React.Ref<HTMLAnchorElement>) => (
                <CardHeadingGroup
                  ref={ref}
                  heading={texts.fundReports}
                  as={InternalLink}
                  to={`/${cmId}/reports/funds`}
                  data-trackid={getTrackId(
                    'go-to-reports-funds',
                    TrackingElement.Link,
                  )}
                />
              )}
            </CardNav>
            <CardNav
              icon="graph_pie"
              data-test="reports-page-presentation-link"
              orientation="horizontal"
            >
              {(ref: React.Ref<HTMLAnchorElement>) => (
                <CardHeadingGroup
                  ref={ref}
                  heading={texts.presentationMode}
                  as={InternalLink}
                  to={`/${cmId}/reports/presentation`}
                  data-trackid={getTrackId(
                    'go-to-reports-presentation',
                    TrackingElement.Link,
                  )}
                />
              )}
            </CardNav>
          </WithGap>
        </CardColumn>
      </Row>
    </WithGap>
  );
};

const StyledCardColumn = styled(CardColumn)`
  display: flex;
`;

const StyledSurfaceWidget = styled(SurfaceWidget)`
  flex-grow: 1;
`;

const List = styled.ul`
  flex: 1;
  list-style: none;
  padding: 0;

  & li:first-child {
    border-top: 1px solid var(--p-color-stroke-subtle-0);
  }
`;
